import { useCallback, useRef } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { COHORTS_KEY, cohortPatientMembershipsPrefix, cohortsListKey } from './use-cohorts';
import type {
  BulkEnrolRequest,
  BulkEnrolResponse,
  BulkEnrolRow,
  CohortMembership,
  EnrolRequest,
} from '@/types/cohort-api';

/**
 * Cohort enrolment writes (PROD-3206), on the pm-react #1242 pattern. Errors are `ApiError`;
 * read them with `serverMessage` / `isOverlapConflict` (cohort-errors.ts).
 */

export const MAX_BULK_PATIENTS = 500;

function invalidateAfterEnrol(qc: QueryClient, patientIds: string[] | 'all') {
  void qc.invalidateQueries({ queryKey: cohortsListKey() });
  if (patientIds === 'all') {
    void qc.invalidateQueries({ queryKey: [...COHORTS_KEY, 'patient-memberships'] });
    return;
  }
  for (const id of patientIds) {
    void qc.invalidateQueries({ queryKey: cohortPatientMembershipsPrefix(id) });
  }
}

/** Single patient. 201/200 = success; 409 = an overlapping membership in this cohort. */
export function useEnrolInCohort() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cohortId, ...body }: EnrolRequest & { cohortId: string }) =>
      post<CohortMembership>(`/api/Cohorts/${cohortId}/memberships`, body),
    onSuccess: (_res, v) => invalidateAfterEnrol(qc, [v.patientId]),
  });
}

export type BulkEnrolInput = Omit<BulkEnrolRequest, 'bulkRequestId'> & { cohortId: string };

interface BulkAttempt {
  input: BulkEnrolInput;
  bulkRequestId: string;
  /** True once this attempt got a success (2xx) response. */
  answered: boolean;
}

/** The terms of a bulk action: everything except the patient list. */
export type BulkEnrolTerms = Omit<BulkEnrolInput, 'patientIds'>;

const sameTerms = (a: BulkEnrolTerms, b: BulkEnrolTerms) =>
  a.cohortId === b.cohortId &&
  a.startsOn === b.startsOn &&
  (a.endsOn ?? null) === (b.endsOn ?? null) &&
  (a.activate ?? true) === (b.activate ?? true) &&
  (a.notes ?? null) === (b.notes ?? null);

const sameInput = (a: BulkEnrolInput, b: BulkEnrolInput) =>
  sameTerms(a, b) &&
  a.patientIds.length === b.patientIds.length &&
  a.patientIds.every((id, i) => id === b.patientIds[i]);

/**
 * Bulk enrol. One `bulkRequestId` (crypto.randomUUID()) per bulk action:
 * - `submit(input)` keeps the previous id when the previous attempt did not succeed (timeout,
 *   network, 5xx, or a 4xx) and the input is unchanged, so a resubmit stays idempotent on the
 *   server. A changed input, or a submit after a success (2xx), starts a new action with a new id.
 * - `retry(patientIds, terms?)` re-sends ONLY those patients. Unchanged terms keep the same id;
 *   changed terms (the user edited the dates or notes) are a new action with a new id.
 * Zero patients, or more than 500, is refused before any request.
 */
export function useBulkEnrol() {
  const qc = useQueryClient();
  const last = useRef<BulkAttempt | null>(null);

  const mutation = useMutation({
    mutationFn: ({ input, bulkRequestId }: Omit<BulkAttempt, 'answered'>) => {
      if (input.patientIds.length === 0) {
        return Promise.reject(new Error('Select at least one patient.'));
      }
      if (input.patientIds.length > MAX_BULK_PATIENTS) {
        return Promise.reject(
          new Error(`You can enrol up to ${MAX_BULK_PATIENTS} patients at a time.`),
        );
      }
      const { cohortId, ...rest } = input;
      return post<BulkEnrolResponse>(`/api/Cohorts/${cohortId}/memberships/bulk`, {
        ...rest,
        bulkRequestId,
      });
    },
    onSuccess: () => invalidateAfterEnrol(qc, 'all'),
  });

  const { mutateAsync } = mutation;

  const send = useCallback(
    (input: BulkEnrolInput, bulkRequestId: string) => {
      const attempt: BulkAttempt = { input, bulkRequestId, answered: false };
      last.current = attempt;
      return mutateAsync({ input, bulkRequestId }).then((res) => {
        attempt.answered = true;
        return res;
      });
    },
    [mutateAsync],
  );

  const submit = useCallback(
    (input: BulkEnrolInput) => {
      const prev = last.current;
      const reuse = prev !== null && !prev.answered && sameInput(prev.input, input);
      return send(input, reuse ? prev.bulkRequestId : crypto.randomUUID());
    },
    [send],
  );

  const retry = useCallback(
    (patientIds: string[], terms?: BulkEnrolTerms) => {
      const prev = last.current;
      if (!prev) return Promise.reject(new Error('Nothing to retry.'));
      const prevTerms: BulkEnrolTerms = {
        cohortId: prev.input.cohortId,
        startsOn: prev.input.startsOn,
        endsOn: prev.input.endsOn,
        activate: prev.input.activate,
        notes: prev.input.notes,
      };
      const nextTerms = terms ?? prevTerms;
      const id = sameTerms(prevTerms, nextTerms) ? prev.bulkRequestId : crypto.randomUUID();
      return send({ ...nextTerms, patientIds }, id);
    },
    [send],
  );

  return { ...mutation, submit, retry };
}

export const isFailedOutcome = (row: BulkEnrolRow) =>
  row.outcome === 'NotFound' || row.outcome === 'Invalid';

/** Only a refused (Invalid) row can succeed on a retry; a NotFound patient stays not found. */
export const isRetryableOutcome = (row: BulkEnrolRow) => row.outcome === 'Invalid';

/**
 * Fold a retry's response into the first one: each retried row replaces its earlier result,
 * and the totals are recounted from the merged rows.
 */
export function mergeBulkResults(
  prev: BulkEnrolResponse,
  next: BulkEnrolResponse,
): BulkEnrolResponse {
  const byId = new Map(next.results.map((r) => [r.patientId, r]));
  const results = prev.results.map((r) => byId.get(r.patientId) ?? r);
  return {
    bulkRequestId: prev.bulkRequestId,
    total: results.length,
    created: results.filter((r) => r.outcome === 'Created').length,
    alreadyMember: results.filter((r) => r.outcome === 'AlreadyMember').length,
    failed: results.filter(isFailedOutcome).length,
    results,
  };
}
